import { useEffect, useState } from "react";
import { allowsStripeOnline } from "../supabase/functions/_shared/billing-entity";
import { formatDate, formatTime } from "./domain/booking";
import {
  cancelManagedBooking,
  configureAppointmentPayment,
  loadManagedBooking,
  type ManagedBooking,
  reconcileAppointmentPayment,
} from "./lib/bookingRepository";
import "./styles.css";
import "./management.css";

export function ManageBooking({
  reference,
  managementToken,
}: {
  reference: string;
  managementToken: string;
}) {
  const [booking, setBooking] = useState<ManagedBooking>();
  const [message, setMessage] = useState<string>();
  const [busy, setBusy] = useState(false);

  async function refreshBooking() {
    const next = await loadManagedBooking(reference, managementToken);
    setBooking(next);
    return next;
  }

  useEffect(() => {
    let active = true;

    async function load() {
      try {
        const parameters = new URLSearchParams(window.location.search);
        const paymentResult = parameters.get("zahlung");
        const sessionId = parameters.get("session_id");

        if (paymentResult === "erfolgreich" && sessionId) {
          const paid = await reconcileAppointmentPayment(sessionId);
          if (active) {
            setMessage(
              paid
                ? "Zahlung und Termin sind bestätigt."
                : "Die Zahlung wird noch verarbeitet. Dein Termin bleibt verbindlich gebucht.",
            );
          }
          clearPaymentParameters();
        } else if (paymentResult === "abgebrochen") {
          if (active) {
            setMessage(
              "Die Onlinezahlung wurde nicht abgeschlossen. Dein Termin bleibt gebucht; du kannst hier erneut bezahlen oder Rechnung wählen.",
            );
          }
          clearPaymentParameters();
        }

        const next = await loadManagedBooking(reference, managementToken);
        if (active) setBooking(next);
      } catch (error) {
        if (active) {
          setMessage(
            error instanceof Error ? error.message : "Die Buchung konnte nicht geladen werden.",
          );
        }
      }
    }

    void load();
    return () => {
      active = false;
    };
  }, [reference, managementToken]);

  async function choosePayment(paymentChoice: "online" | "invoice") {
    if (!booking || booking.status !== "confirmed") return;
    setBusy(true);
    setMessage(undefined);
    try {
      const result = await configureAppointmentPayment(
        { reference, managementToken, source: "live" },
        paymentChoice,
      );
      if (result.checkoutUrl) {
        window.location.assign(result.checkoutUrl);
        return;
      }
      await refreshBooking();
      setMessage(
        result.paymentChoice === "not_required"
          ? "Für diesen Termin ist keine Zahlung erforderlich."
          : result.paymentStatus === "paid"
            ? "Dieser Termin ist bereits bezahlt."
            : "Rechnung ist gewählt. Du erhältst sie separat.",
      );
    } catch (error) {
      setMessage(
        error instanceof Error
          ? error.message
          : "Der Zahlungsweg konnte gerade nicht vorbereitet werden.",
      );
    } finally {
      setBusy(false);
    }
  }

  async function cancelBooking() {
    if (booking?.status !== "confirmed") return;
    const confirmed = window.confirm(
      "Möchtest du diesen Termin wirklich stornieren? Der Zeitpunkt wird anschließend wieder freigegeben.",
    );
    if (!confirmed) return;

    setBusy(true);
    setMessage(undefined);
    try {
      await cancelManagedBooking(reference, managementToken);
      setBooking((current) => (current ? { ...current, status: "cancelled" } : current));
      setMessage("Der Termin wurde storniert. Die Kalender-Einladung wird ebenfalls aufgehoben.");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Die Stornierung ist fehlgeschlagen.");
    } finally {
      setBusy(false);
    }
  }

  const stripeAllowed =
    booking?.stripeOnlineAllowed === true || allowsStripeOnline(booking?.billingEntity);
  const practitioner = managedBookingPractitioner(booking);

  return (
    <main className="management-page">
      <a className="management-brand" href={practitioner.bookingUrl}>
        <b>{practitioner.initial}</b>
        <span>{practitioner.brand}</span>
      </a>

      <section className="management-card">
        <p className="kicker">Buchung verwalten</p>
        <h1>{booking?.status === "cancelled" ? "Termin storniert" : "Dein Termin"}</h1>

        {!booking && !message && <p className="management-loading">Buchung wird geladen …</p>}
        {message && <div className="notice">{message}</div>}

        {booking && (
          <>
            <div className="management-status" data-status={booking.status}>
              {booking.status === "confirmed" ? "Verbindlich gebucht" : "Storniert"}
            </div>
            <dl className="management-details">
              <div>
                <dt>Referenz</dt>
                <dd>{booking.reference}</dd>
              </div>
              <div>
                <dt>Termin</dt>
                <dd>{booking.offerTitle}</dd>
              </div>
              <div>
                <dt>Datum</dt>
                <dd>{formatDate(booking.startsAt)}</dd>
              </div>
              <div>
                <dt>Uhrzeit</dt>
                <dd>{formatTime(booking.startsAt)} Uhr</dd>
              </div>
              <div>
                <dt>Dauer</dt>
                <dd>{booking.variantLabel}</dd>
              </div>
              <div>
                <dt>Preis</dt>
                <dd>{booking.priceLabel}</dd>
              </div>
              <div>
                <dt>Ort</dt>
                <dd>
                  {booking.locationName}
                  <small>{booking.addressSummary}</small>
                </dd>
              </div>
            </dl>

            {booking.status === "confirmed" ? (
              <section className="management-payment" aria-labelledby="management-payment-title">
                <p className="kicker">Zahlung</p>
                <h2 id="management-payment-title">
                  {stripeAllowed ? "Wie möchtest du den Termin begleichen?" : "Rechnung"}
                </h2>
                {booking.paymentStatus === "paid" ? (
                  <div className="management-payment__state is-paid">
                    <strong>Bezahlt</strong>
                    <span>{paymentMethodLabel(booking.paymentMethod)}</span>
                  </div>
                ) : booking.paymentStatus === "not_required" ? (
                  <div className="management-payment__state is-paid">
                    <strong>Keine Zahlung erforderlich</strong>
                  </div>
                ) : stripeAllowed && booking.paymentChoice === "invoice" ? (
                  <div className="management-payment__state">
                    <strong>Rechnung gewählt</strong>
                    <span>Die Rechnung erhältst du separat.</span>
                    <button type="button" disabled={busy} onClick={() => void choosePayment("online")}>
                      Stattdessen online bezahlen
                    </button>
                  </div>
                ) : stripeAllowed ? (
                  <div className="management-payment__choices">
                    <button type="button" disabled={busy} onClick={() => void choosePayment("online")}>
                      <strong>Direkt online bezahlen</strong>
                      <span>Sicher über Stripe.</span>
                    </button>
                    <button type="button" disabled={busy} onClick={() => void choosePayment("invoice")}>
                      <strong>Rechnung erhalten</strong>
                      <span>Du erhältst sie separat.</span>
                    </button>
                  </div>
                ) : (
                  <div className="management-payment__state">
                    <strong>Die Rechnung kommt separat.</strong>
                    <span>{invoiceExplanation(booking.billingEntity)}</span>
                    {booking.paymentChoice !== "invoice" ? (
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => void choosePayment("invoice")}
                      >
                        Rechnung bestätigen
                      </button>
                    ) : null}
                  </div>
                )}
              </section>
            ) : null}

            <div className="management-actions">
              {booking.status === "confirmed" && (
                <button
                  type="button"
                  className="button button--primary"
                  onClick={cancelBooking}
                  disabled={busy}
                >
                  {busy ? "Wird bearbeitet …" : "Termin stornieren"}
                </button>
              )}
              <a
                className="button button--ghost"
                href={`mailto:${practitioner.email}?subject=${encodeURIComponent(
                  `Umbuchung ${booking.reference}`,
                )}`}
              >
                Änderung anfragen
              </a>
              <a className="button button--ghost" href={practitioner.bookingUrl}>
                Neue Buchung
              </a>
            </div>
          </>
        )}
      </section>
    </main>
  );
}

function managedBookingPractitioner(booking?: ManagedBooking) {
  if (booking?.resourceSlug === "katrin") {
    return {
      initial: "K",
      brand: "Booking · Katrin Schörling",
      email: "booking@katrinschoerling.com",
      bookingUrl: "https://book.katrinschoerling.com/termine",
    };
  }

  return {
    initial: "R",
    brand: "Booking · Rayk Schörling",
    email: "booking@raykschoerling.com",
    bookingUrl: "https://book.raykschoerling.com/termine",
  };
}

function invoiceExplanation(entity?: ManagedBooking["billingEntity"]) {
  if (entity === "praxis") {
    return "Die Rechnung erhältst du separat über die Naturheilpraxis.";
  }
  return "Die Rechnung erhältst du separat.";
}

function clearPaymentParameters() {
  const url = new URL(window.location.href);
  url.searchParams.delete("zahlung");
  url.searchParams.delete("session_id");
  window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
}

function paymentMethodLabel(value?: string | null) {
  const labels: Record<string, string> = {
    card: "Karte",
    klarna: "Klarna",
    paypal: "PayPal",
    sepa_debit: "SEPA-Lastschrift",
    bank_transfer: "Überweisung",
  };
  return value ? labels[value] ?? value : "Onlinezahlung über Stripe";
}
